import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateAdapterOptions } from '@angular/material-moment-adapter';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { padTimePart, parseTimeValue } from 'src/app/shared/compatibility/data-type';
import { DataTypeConfig, DataTypeMap } from 'src/app/shared/compatibility/filter.models';
import { ValueDataType } from '../../../services/api.services';

export enum DataInputUpdateOn {
  Change = 'change',
  Blur = 'blur'
};

@Component({
  selector: 'app-data-input',
  templateUrl: './data-input.component.html',
  styleUrls: ['./data-input.component.scss'],
    providers: [
        {
            provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
            useValue: <MatMomentDateAdapterOptions>{
                useUtc: true
            }
        }
    ],
  standalone: false
})
export class SharedDataInputComponent implements OnChanges {
  readonly ValueDataType = ValueDataType;

  @Input() appearance = '';
  @Input() locale = 'en-US';

  @Input() inputClass = 'trg-outline';

  @Input() type: ValueDataType;

  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  @Input() updateOn: string | DataInputUpdateOn = DataInputUpdateOn.Change;
  @Input() disabled = false;

  @Input() showTimePicker = false;
  @Input() showSeconds = false;

  dataType: DataTypeConfig;

  date: moment.Moment = moment();

  valueFormControl: FormControl;
  valueFormControlSub: Subscription;

  hourPart: string = '00';
  minutePart: string = '00';
  secondPart: string = '00';
  canSetMinutes = false;
  canSetSeconds = false;

  ensureFormControl() {
    if (!this.valueFormControl) {
      this.createFormControl();
    }
  }

  constructor() {
  }

  private createFormControl() {
    if (this.valueFormControlSub)
      this.valueFormControlSub.unsubscribe();

    this.valueFormControl = new FormControl(this.value, { updateOn: <any>this.updateOn });

    this.valueFormControlSub = this.valueFormControl.valueChanges.pipe(
      debounceTime(600),
      tap(v => {
        this.valueChange.emit(v);
      })
    ).subscribe();
  }

  ngOnInit() {
    this.ensureFormControl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.updateOn) {
      this.createFormControl();
    }

    if (changes.type) {
      this.dataType = DataTypeMap[this.type];
    }

    if (changes.type || changes.value) {
      this.ensureFormControl();

      if (this.dataType.id === ValueDataType.DateOnly
        || this.dataType.id === ValueDataType.DateTime) {
        let m = moment.utc(this.value).utc(true);
        this.date = m.isValid() ? m : null;
      }

      if (this.dataType.id === ValueDataType.DateTime
        || this.dataType.id === ValueDataType.TimeOnly) {
        this.canSetMinutes = this.dataType.id !== ValueDataType.TimeOnly;
        this.canSetSeconds = this.canSetMinutes && this.showSeconds;

        if (this.value) {
          let timeValue = parseTimeValue(this.value, this.canSetSeconds);

          this.hourPart = timeValue.hourPart;
          this.minutePart = timeValue.minutePart;
          this.secondPart = timeValue.secondPart;
        }
      }

      // All other data types use free input, so we want to use the form control for them
      this.valueFormControl.reset(this.value, { emitEvent: false });
    }

    if (changes.disabled) {
      if (this.disabled)
        this.valueFormControl.disable();
      else
        this.valueFormControl.enable();
    }
  }

  onValueChange() {
    this.valueFormControl.setValue(this.value);
  }

  onDateChange() {
    this.value = this.date.clone().utc(true).toJSON();
    this.onValueChange();
  }

  onTimeChange() {
    let hourPart = Number(this.hourPart);
    let minutePart = Number(this.minutePart);
    let secondPart = Number(this.secondPart);
    
    if (isNaN(hourPart)) {
      this.hourPart = '00';
    }
    else {
      hourPart = Math.min(Math.max(hourPart, 0), 23);
      this.hourPart = padTimePart(hourPart);
    }

    if (isNaN(minutePart)) {
      this.minutePart = '00';
    }
    else {
      minutePart = Math.min(Math.max(minutePart, 0), 59);
      this.minutePart = padTimePart(minutePart);
    }
    
    if (isNaN(secondPart)) {
      this.secondPart = '00';
    }
    else {
      secondPart = Math.min(Math.max(secondPart, 0), 59);
      this.secondPart = padTimePart(secondPart);
    }

    if (this.dataType.id == ValueDataType.TimeOnly) {
      this.value = `${this.hourPart}:${this.minutePart}:${this.secondPart}`;
    }
    else {
      this.value = this.date.clone().utc(true)
        .set('hour', hourPart)
        .set('minute', minutePart)
        .set('second', secondPart).toJSON();
    }

    this.onValueChange();
  }

  updateTime(addHour: number, addMinute: number, addSecond: number) {
    if (this.disabled)
      return;

    let hourPart = parseInt(this.hourPart) + addHour;
    let minutePart = parseInt(this.minutePart) + addMinute;
    let secondPart = parseInt(this.secondPart) + addSecond;

    if (hourPart < 0)
      hourPart = 24 + hourPart;

    if (minutePart < 0)
      minutePart = 60 + minutePart;
      
    if (secondPart < 0)
      secondPart = 60 + secondPart;

    hourPart %= 24;
    minutePart %= 60;
    secondPart %= 60;

    this.hourPart = padTimePart(hourPart);
    this.minutePart = padTimePart(minutePart);
    this.secondPart = padTimePart(secondPart);

    this.onTimeChange();
  }
};
